import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";

/* Simple table utility */
function DataTable({ columns, rows, testid }) {
  return (
    <div className="border border-black/10 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-black/10">
            {columns.map((c) => (
              <th key={c.k} className="text-left px-4 py-3 overline">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td className="p-6 text-zinc-500" colSpan={columns.length}>No data.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-black/10 last:border-b-0 hover:bg-zinc-50">
              {columns.map((c) => (
                <td key={c.k} className="px-4 py-3 align-top">
                  {c.render ? c.render(r) : (r[c.k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="overline animate-pulse">Loading…</div>;
  const kpis = [
    ["Revenue", money(s.revenue_cents)],
    ["Orders", s.total_orders],
    ["Customers", s.total_customers],
    ["Products", s.total_products],
    ["Active affiliates", s.total_affiliates],
    ["Pending applications", s.pending_applications],
    ["Pending commissions", s.pending_commissions],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {kpis.map(([k, v]) => (
        <div key={k} className="border border-black/10 p-4">
          <div className="overline">{k}</div>
          <div className="font-display text-2xl font-bold mt-2">{v}</div>
        </div>
      ))}
    </div>
  );
}

function Products() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [addForm, setAddForm] = useState({ slug: "", name: "", category_slug: "", price: "", image_url: "" });
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/products").then((r) => setRows(r.data));
  const loadFeatured = () => api.get("/admin/settings/featured").then((r) => setFeatured(r.data.slugs || []));
  useEffect(() => {
    load(); loadFeatured();
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const saveRow = async (row, next) => {
    const calls = [];
    if (next.price_cents !== row.price_cents) {
      calls.push(api.patch(`/admin/products/${row.id}/price`, { price_cents: next.price_cents }));
    }
    if (next.active !== row.active) {
      calls.push(api.patch(`/admin/products/${row.id}/active`, { active: next.active }));
    }
    const wasFeatured = featured.includes(row.slug);
    let nextFeatured = featured;
    if (next.featured !== wasFeatured) {
      nextFeatured = next.featured
        ? [...featured, row.slug].slice(0, 8)
        : featured.filter((s) => s !== row.slug);
      calls.push(api.put("/admin/settings/featured", { slugs: nextFeatured }));
    }
    await Promise.all(calls);
    if (nextFeatured !== featured) setFeatured(nextFeatured);
    const { toast } = await import("sonner");
    toast.success(`Saved ${row.name}`);
    load();
  };
  const removeProduct = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/products/${row.id}`);
    const { toast } = await import("sonner");
    toast.success(`Deleted ${row.name}`);
    load(); loadFeatured();
  };
  const addProduct = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const price_cents = Math.max(0, Math.round(parseFloat(addForm.price || 0) * 100));
      const mg = ""; // free-form; admin can edit later
      const body = {
        slug: addForm.slug.trim(),
        name: addForm.name.trim(),
        category_slug: addForm.category_slug,
        subtitle: "Individual research vial",
        short_description: `${addForm.name.trim()}. For research use only.`,
        description: `${addForm.name.trim()}. For research use only. Not for human or veterinary use.`,
        price_cents,
        image_url: addForm.image_url.trim() || "/products/reatrutide-20mg.png",
        purity: "≥ 99%",
        storage: "-20°C, protect from light",
        active: true,
        variants: [{ sku: ("PRMU-" + addForm.slug.toUpperCase().replace(/-/g, "")).slice(0, 32), label: "", price_cents, stock: 0 }],
      };
      await api.post("/admin/products", body);
      const { toast } = await import("sonner");
      toast.success("Product added");
      setAddForm({ slug: "", name: "", category_slug: "", price: "", image_url: "" });
      load();
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post("/admin/products/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const { toast } = await import("sonner");
      toast.success(`Import: ${data.created} created · ${data.updated} updated${data.errors.length ? ` · ${data.errors.length} errors` : ""}`);
      load();
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setUploading(false); }
  };
  const downloadTemplate = async () => {
    const res = await api.get("/admin/products/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "prmu-products-template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={addProduct} className="border border-white/10 rounded-xl p-4 grid md:grid-cols-6 gap-3" data-testid="admin-add-product">
        <div className="md:col-span-6 overline">Add product</div>
        <input required placeholder="slug (e.g. glp-1-20mg)" value={addForm.slug} onChange={(e) => setAddForm({ ...addForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className="md:col-span-2 border border-white/15 rounded px-3 py-2 text-sm font-mono" data-testid="add-slug" />
        <input required placeholder="Name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="md:col-span-2 border border-white/15 rounded px-3 py-2 text-sm" data-testid="add-name" />
        <select required value={addForm.category_slug} onChange={(e) => setAddForm({ ...addForm, category_slug: e.target.value })} className="border border-white/15 rounded px-3 py-2 text-sm" data-testid="add-category">
          <option value="">Category…</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input required placeholder="Price ($)" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value.replace(/[^0-9.]/g, "") })} className="border border-white/15 rounded px-3 py-2 text-sm font-mono" data-testid="add-price" />
        <input placeholder="Image URL (optional, e.g. /products/foo.png)" value={addForm.image_url} onChange={(e) => setAddForm({ ...addForm, image_url: e.target.value })} className="md:col-span-5 border border-white/15 rounded px-3 py-2 text-sm" data-testid="add-image" />
        <button className="btn-prm !py-2" disabled={adding} data-testid="add-submit">{adding ? "Adding…" : "Add"}</button>
      </form>

      <div className="border border-white/10 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div>
          <div className="overline">Bulk import</div>
          <div className="text-sm text-zinc-500 mt-1">Upload CSV — existing rows matched by <span className="font-mono">slug</span>.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-outline" onClick={downloadTemplate} data-testid="admin-download-template">Download template</button>
          <label className={`btn-prm cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            {uploading ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="admin-import-input" />
          </label>
        </div>
      </div>

      <div className="border border-white/10 rounded-xl p-4">
        <div className="overline mb-2">Best Sellers (Home page)</div>
        <div className="text-sm text-zinc-500 mb-3">Toggle the star to feature a product on the homepage. Max 8. Order = order clicked.</div>
        {featured.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {featured.map((s, i) => (
              <span key={s} className="chip chip-accent">
                <span className="font-mono text-[10px] mr-1 opacity-70">#{i + 1}</span>{s}
              </span>
            ))}
          </div>
        ) : <div className="text-xs text-zinc-500 font-mono">defaults · Retatrutide · NAD+ · BPC-157 + TB-500 · KLOW</div>}
      </div>

      <div className="border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-products">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {["Product", "Category", "Price", "Active", "Featured", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ProductAdminRow
                key={r.id}
                row={r}
                isFeatured={featured.includes(r.slug)}
                onSave={saveRow}
                onDelete={removeProduct}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductAdminRow({ row, isFeatured, onSave, onDelete }) {
  const [price, setPrice] = useState((row.price_cents / 100).toFixed(2));
  const [active, setActive] = useState(row.active);
  const [featured, setFeatured] = useState(isFeatured);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPrice((row.price_cents / 100).toFixed(2));
    setActive(row.active);
    setFeatured(isFeatured);
  }, [row.price_cents, row.active, isFeatured]);

  const priceCents = Math.max(0, Math.round(parseFloat(price || 0) * 100));
  const dirty =
    priceCents !== row.price_cents ||
    active !== row.active ||
    featured !== isFeatured;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(row, { price_cents: priceCents, active, featured });
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const reset = () => {
    setPrice((row.price_cents / 100).toFixed(2));
    setActive(row.active);
    setFeatured(isFeatured);
  };

  return (
    <tr className={`border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] ${dirty ? "bg-blue-500/[0.04]" : ""}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {row.image_url && <img src={row.image_url} alt="" className="w-10 h-10 rounded object-cover border border-white/10" />}
          <div className="min-w-0">
            <div className="font-display-plain font-semibold truncate">{row.name}</div>
            <div className="text-xs font-mono text-zinc-500 truncate">{row.slug}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-xs font-mono text-zinc-400">{row.category_slug}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-xs">$</span>
          <input value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} className="w-24 border border-white/15 rounded px-2 py-1 font-mono" data-testid={`price-${row.slug}`} />
        </div>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => setActive((v) => !v)}
          className={`relative inline-flex h-6 w-11 rounded-full border transition-colors ${active ? "bg-blue-500 border-blue-500" : "bg-white/5 border-white/20"}`}
          data-testid={`active-${row.slug}`}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform mt-[1px] ${active ? "translate-x-5" : "translate-x-[2px]"}`} />
        </button>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => setFeatured((v) => !v)}
          className={`text-lg leading-none ${featured ? "text-blue-400" : "text-zinc-600 hover:text-zinc-400"}`}
          data-testid={`feature-${row.slug}`}
          title="Toggle in Best Sellers (save to apply)"
        >
          {featured ? "★" : "☆"}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-prm !py-1 !px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`product-save-${row.slug}`}
          >
            {saving ? "…" : "Save"}
          </button>
          {dirty && (
            <button
              onClick={reset}
              className="text-xs text-zinc-500 hover:text-white"
              data-testid={`product-reset-${row.slug}`}
              title="Discard changes"
            >
              Reset
            </button>
          )}
          <button
            onClick={() => onDelete(row)}
            className="text-zinc-500 hover:text-red-400 transition-colors text-lg leading-none px-1"
            title="Delete product"
            data-testid={`delete-${row.slug}`}
          >
            ×
          </button>
        </div>
      </td>
    </tr>
  );
}


function Orders() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    load();
  };
  return (
    <DataTable
      testid="admin-orders"
      columns={[
        { k: "order_number", label: "Order", render: (r) => <span className="font-mono text-xs">{r.order_number}</span> },
        { k: "customer_email", label: "Customer" },
        { k: "total_cents", label: "Total", render: (r) => money(r.total_cents) },
        { k: "status", label: "Status", render: (r) => (
            <select value={r.status} onChange={(e) => update(r.id, e.target.value)} className="border border-black/20 px-2 py-1 text-xs font-mono" data-testid={`admin-order-status-${r.order_number}`}>
              {["pending", "paid", "processing", "shipped", "delivered", "cancelled", "refunded"].map((s) => <option key={s}>{s}</option>)}
            </select>
          )
        },
        { k: "created_at", label: "Placed", render: (r) => new Date(r.created_at).toLocaleString() },
      ]}
      rows={rows}
    />
  );
}

function Customers() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setRows(r.data)); }, []);
  return (
    <DataTable
      testid="admin-customers"
      columns={[
        { k: "name", label: "Name" },
        { k: "email", label: "Email", render: (r) => <span className="font-mono text-xs">{r.email}</span> },
        { k: "role", label: "Role", render: (r) => <span className="overline">{r.role}</span> },
        { k: "created_at", label: "Joined", render: (r) => new Date(r.created_at).toLocaleDateString() },
      ]}
      rows={rows}
    />
  );
}

function Affiliates() {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const load = () => api.get("/admin/affiliates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/affiliates/${id}`, patch);
      await load();
      const { toast } = await import("sonner");
      toast.success("Affiliate updated");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSavingId(null); }
  };

  return (
    <div className="border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-affiliates">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {["Partner", "Slug", "Coupon", "Tier", "Rate %", "Cookie days", "Status", "Actions"].map((h) => (
              <th key={h} className="text-left px-4 py-3 overline">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <AffiliateRow key={r.id} row={r} saving={savingId === r.id} onSave={save} />
          ))}
          {rows.length === 0 && (
            <tr><td className="px-4 py-6 text-zinc-500" colSpan={8}>No approved partners yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AffiliateRow({ row, saving, onSave }) {
  const [rate, setRate] = useState((row.commission_rate_bps / 100).toFixed(1));
  const [status, setStatus] = useState(row.status || "active");
  const [tier, setTier] = useState(row.tier || "Standard");
  const [coupon, setCoupon] = useState(row.coupon_code || "");
  const [cookie, setCookie] = useState(row.cookie_days || 30);
  useEffect(() => {
    setRate((row.commission_rate_bps / 100).toFixed(1));
    setStatus(row.status || "active");
    setTier(row.tier || "Standard");
    setCoupon(row.coupon_code || "");
    setCookie(row.cookie_days || 30);
  }, [row]);

  const rateBps = Math.max(0, Math.min(10000, Math.round(parseFloat(rate || 0) * 100)));
  const cookieDays = Math.max(1, Math.min(365, parseInt(cookie || 30, 10)));
  const dirty =
    rateBps !== row.commission_rate_bps ||
    status !== (row.status || "active") ||
    tier !== (row.tier || "Standard") ||
    (coupon || "") !== (row.coupon_code || "") ||
    cookieDays !== (row.cookie_days || 30);

  const active = status === "active";
  const displayName = row.name || row.email || `partner-${row.id.slice(0, 6)}`;

  return (
    <tr className={`border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] ${dirty ? "bg-blue-500/[0.04]" : ""}`}>
      <td className="px-4 py-3">
        <div className="font-display-plain font-semibold">{displayName}</div>
        <div className="text-xs font-mono text-zinc-500 truncate">{row.email || row.user_id}</div>
      </td>
      <td className="px-4 py-3">
        <a href={`/r/${row.referral_slug}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-blue-400 hover:underline">/r/{row.referral_slug}</a>
      </td>
      <td className="px-4 py-3">
        <input
          value={coupon}
          onChange={(e) => setCoupon(e.target.value.toUpperCase())}
          placeholder="—"
          className="w-24 font-mono text-xs border border-white/15 rounded px-2 py-1 bg-white/[0.03]"
          data-testid={`aff-coupon-${row.id}`}
        />
      </td>
      <td className="px-4 py-3">
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="border border-white/15 rounded px-2 py-1 text-xs bg-white/[0.03]"
          data-testid={`aff-tier-${row.id}`}
        >
          {["Standard", "Silver", "Gold", "Platinum"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <input
            type="number"
            step="0.5"
            value={rate}
            onChange={(e) => setRate(e.target.value.replace(/[^0-9.]/g, ""))}
            className="w-16 border border-white/15 rounded px-2 py-1 font-mono text-sm bg-white/[0.03]"
            data-testid={`aff-rate-${row.id}`}
          />
          <span className="text-xs text-zinc-500">%</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <input
          type="number"
          value={cookie}
          onChange={(e) => setCookie(e.target.value.replace(/\D/g, ""))}
          className="w-16 border border-white/15 rounded px-2 py-1 font-mono text-sm bg-white/[0.03]"
          data-testid={`aff-cookie-${row.id}`}
        />
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => setStatus(active ? "paused" : "active")}
          className={`relative inline-flex h-6 w-11 rounded-full border transition-colors ${active ? "bg-blue-500 border-blue-500" : "bg-white/5 border-white/20"}`}
          data-testid={`aff-active-${row.id}`}
          title={active ? "Active — click to pause" : "Paused — click to activate"}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform mt-[1px] ${active ? "translate-x-5" : "translate-x-[2px]"}`} />
        </button>
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => onSave(row.id, {
            commission_rate_bps: rateBps,
            status,
            tier,
            coupon_code: coupon || null,
            cookie_days: cookieDays,
          })}
          disabled={!dirty || saving}
          className="btn-prm !py-1 !px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`aff-save-${row.id}`}
        >
          {saving ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function Applications() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/affiliate-applications").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, approve) => {
    const slug = approve ? prompt("Referral slug?") : null;
    const coupon = approve ? prompt("Coupon code (optional)?") : null;
    await api.post(`/admin/affiliate-applications/${id}/decision`, { approve, referral_slug: slug || undefined, coupon_code: coupon || undefined });
    load();
  };
  return (
    <DataTable
      testid="admin-applications"
      columns={[
        { k: "full_name", label: "Name" },
        { k: "email", label: "Email" },
        { k: "audience", label: "Audience" },
        { k: "status", label: "Status", render: (r) => <span className="overline">{r.status}</span> },
        {
          k: "actions", label: "Actions", render: (r) => r.status === "pending" ? (
            <div className="flex gap-2">
              <button className="btn-outline !py-1 !px-2 text-xs" onClick={() => decide(r.id, true)} data-testid={`approve-${r.id}`}>Approve</button>
              <button className="btn-outline !py-1 !px-2 text-xs" onClick={() => decide(r.id, false)}>Reject</button>
            </div>
          ) : "—"
        },
      ]}
      rows={rows}
    />
  );
}

function Commissions() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/commissions").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { await api.patch(`/admin/commissions/${id}`, { status }); load(); };
  return (
    <DataTable
      testid="admin-commissions"
      columns={[
        { k: "order_number", label: "Order", render: (r) => <span className="font-mono text-xs">{r.order_number}</span> },
        { k: "amount_cents", label: "Amount", render: (r) => money(r.amount_cents) },
        { k: "rate_bps", label: "Rate", render: (r) => `${(r.rate_bps / 100).toFixed(1)}%` },
        { k: "status", label: "Status", render: (r) => (
            <select value={r.status} onChange={(e) => update(r.id, e.target.value)} className="border border-black/20 px-2 py-1 text-xs font-mono">
              {["pending", "approved", "payable", "paid", "reversed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          )
        },
      ]}
      rows={rows}
    />
  );
}

function Discounts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", kind: "percent", value: 10, active: true });
  const load = () => api.get("/admin/discounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/admin/discounts", { ...form, code: form.code.toUpperCase(), value: Number(form.value) });
    setForm({ code: "", kind: "percent", value: 10, active: true });
    load();
  };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="border border-black/10 p-4 flex flex-wrap gap-3 items-end" data-testid="admin-new-discount">
        <label className="block">
          <div className="overline mb-1">Code</div>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required className="border border-black/20 px-3 py-2 text-sm font-mono" />
        </label>
        <label className="block">
          <div className="overline mb-1">Type</div>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="border border-black/20 px-3 py-2 text-sm">
            <option value="percent">Percent</option>
            <option value="fixed">Fixed (cents)</option>
          </select>
        </label>
        <label className="block">
          <div className="overline mb-1">Value</div>
          <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="border border-black/20 px-3 py-2 text-sm w-24" />
        </label>
        <button className="btn-prm">Add code</button>
      </form>
      <DataTable
        columns={[
          { k: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
          { k: "kind", label: "Kind" },
          { k: "value", label: "Value" },
          { k: "uses", label: "Uses" },
          { k: "active", label: "Active", render: (r) => <span className="overline">{r.active ? "yes" : "no"}</span> },
        ]}
        rows={rows}
      />
    </div>
  );
}

function COAs() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", batch_number: "", title: "", document_url: "", tested_at: "", lab_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const load = () => api.get("/admin/coas").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/products/${form.product_id}/coas`, {
        batch_number: form.batch_number,
        title: form.title,
        document_url: form.document_url,
        tested_at: form.tested_at || null,
        lab_name: form.lab_name || null,
      });
      setForm({ product_id: "", batch_number: "", title: "", document_url: "", tested_at: "", lab_name: "" });
      const { toast } = await import("sonner");
      toast.success("COA added to product");
      load();
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="border border-white/10 p-6 rounded-xl grid md:grid-cols-2 gap-4" data-testid="admin-add-coa">
        <div className="md:col-span-2">
          <div className="overline">Add COA to product</div>
          <div className="text-sm text-zinc-500 mt-1">Shows up automatically on that product's page. If the batch number is new, it will be created.</div>
        </div>
        <label className="block">
          <div className="overline mb-1">Product</div>
          <select
            value={form.product_id}
            onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            required
            className="w-full border border-white/15 px-3 py-2 bg-white/[0.03] text-sm rounded-lg outline-none focus:border-blue-500"
            data-testid="coa-product-select"
          >
            <option value="">Select a product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <CoaField label="Batch number" v={form.batch_number} on={(v) => setForm({ ...form, batch_number: v })} placeholder="PRMU-XXXX-B01" required tid="coa-batch" />
        <CoaField label="COA title" v={form.title} on={(v) => setForm({ ...form, title: v })} placeholder="e.g. HPLC + MS report" required tid="coa-title" full />
        <CoaField label="Document URL" v={form.document_url} on={(v) => setForm({ ...form, document_url: v })} placeholder="https://..." required tid="coa-url" full />
        <CoaField label="Tested on (YYYY-MM-DD)" v={form.tested_at} on={(v) => setForm({ ...form, tested_at: v })} placeholder="2026-02-01" tid="coa-tested" />
        <CoaField label="Lab name" v={form.lab_name} on={(v) => setForm({ ...form, lab_name: v })} placeholder="Independent Analytical Labs" tid="coa-lab" />
        <div className="md:col-span-2">
          <button className="btn-prm" disabled={submitting} data-testid="coa-submit">{submitting ? "Adding…" : "Add COA"}</button>
        </div>
      </form>
      <DataTable
        testid="admin-coas"
        columns={[
          { k: "title", label: "Title" },
          { k: "product_name", label: "Product" },
          { k: "batch_number", label: "Batch", render: (r) => <span className="font-mono text-xs">{r.batch_number}</span> },
          { k: "lab_name", label: "Lab" },
          { k: "document_url", label: "Doc", render: (r) => <a href={r.document_url} className="link-underline text-xs" target="_blank" rel="noreferrer">Open</a> },
        ]}
        rows={rows}
      />
    </div>
  );
}

function CoaField({ label, v, on, placeholder, required, tid, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="overline mb-1">{label}</div>
      <input
        value={v}
        required={required}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="w-full border border-white/15 px-3 py-2 bg-white/[0.03] text-sm rounded-lg outline-none focus:border-blue-500"
        data-testid={tid}
      />
    </label>
  );
}

function Batches() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/batches").then((r) => setRows(r.data)); }, []);
  return (
    <DataTable
      columns={[
        { k: "batch_number", label: "Batch", render: (r) => <span className="font-mono text-xs">{r.batch_number}</span> },
        { k: "product_name", label: "Product" },
        { k: "manufactured_at", label: "Manufactured" },
        { k: "expires_at", label: "Expires" },
      ]}
      rows={rows}
    />
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setRows(r.data)); }, []);
  return (
    <DataTable
      testid="admin-audit-log"
      columns={[
        { k: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
        { k: "actor_email", label: "Actor" },
        { k: "action", label: "Action", render: (r) => <span className="font-mono text-xs">{r.action}</span> },
        { k: "target", label: "Target", render: (r) => <span className="font-mono text-xs">{r.target || "—"}</span> },
      ]}
      rows={rows}
    />
  );
}

function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/integrations").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="overline animate-pulse">Loading…</div>;
  const items = [
    ["Payment processor", s.payment_provider],
    ["GoHighLevel CRM", s.gohighlevel],
    ["Shipping provider", s.shipping_provider],
    ["Accounting provider", s.accounting_provider],
  ];
  return (
    <div className="space-y-4">
      <div className="overline">Integration settings</div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(([k, v]) => (
          <div key={k} className="border border-black/10 p-6">
            <div className="font-display font-semibold">{k}</div>
            <div className="text-xs font-mono text-zinc-500 mt-2">
              Provider: {v?.provider || "—"} · Status: {v?.connected ? "connected" : "placeholder"}
            </div>
            <p className="text-sm text-zinc-600 mt-3">Adapter interface is in place. Connect via server-side keys once ready.</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Inventory() {
  const [data, setData] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const load = () => api.get("/admin/inventory").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const adjust = async (id, patch) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/inventory/${id}`, patch);
      await load();
      const { toast } = await import("sonner");
      toast.success("Inventory saved");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSavingId(null); }
  };

  if (!data) return <div className="overline animate-pulse">Loading…</div>;
  const s = data.summary;
  const kpis = [
    ["Total units", s.total_units],
    ["Retail value", money(s.retail_value_cents)],
    ["Cost value", money(s.cost_value_cents)],
    ["Potential profit", money(s.potential_profit_cents)],
  ];

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(([k, v]) => (
          <div key={k} className="border border-white/10 rounded-xl p-4">
            <div className="overline">{k}</div>
            <div className="font-display text-2xl mt-2">{v}</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {["Product", "Stock", "Cost", "Sell", "Retail value", "Margin/unit", "Margin %", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <InventoryRow key={r.product_id} row={r} onSave={adjust} saving={savingId === r.product_id} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InventoryRow({ row, onSave, saving }) {
  const [stock, setStock] = useState(row.stock);
  const [cost, setCost] = useState((row.cost_cents / 100).toFixed(2));
  useEffect(() => { setStock(row.stock); setCost((row.cost_cents / 100).toFixed(2)); }, [row]);
  const dirty =
    Number(stock) !== row.stock ||
    Math.round(parseFloat(cost || 0) * 100) !== row.cost_cents;
  const save = () => onSave(row.product_id, {
    stock: Number(stock),
    cost_cents: Math.max(0, Math.round(parseFloat(cost || 0) * 100)),
  });
  const reset = () => { setStock(row.stock); setCost((row.cost_cents / 100).toFixed(2)); };
  return (
    <tr className={`border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] ${dirty ? "bg-blue-500/[0.04]" : ""}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {row.image_url && <img src={row.image_url} alt="" className="w-10 h-10 rounded object-cover border border-white/10" />}
          <div className="min-w-0">
            <div className="font-display-plain font-semibold truncate">{row.name}</div>
            <div className="text-xs font-mono text-zinc-500">{row.variant_sku} · {row.variant_label}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button onClick={() => setStock((v) => Math.max(0, Number(v) - 1))} className="w-7 h-7 rounded border border-white/15 hover:border-white/40" data-testid={`inv-dec-${row.slug}`}>-</button>
          <input value={stock} onChange={(e) => setStock(e.target.value.replace(/\D/g, ""))} className="w-16 text-center border border-white/15 rounded px-2 py-1 font-mono" data-testid={`inv-stock-${row.slug}`} />
          <button onClick={() => setStock((v) => Number(v) + 1)} className="w-7 h-7 rounded border border-white/15 hover:border-white/40" data-testid={`inv-inc-${row.slug}`}>+</button>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="text-zinc-500 text-xs">$</span>
          <input value={cost} onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))} className="w-20 border border-white/15 rounded px-2 py-1 font-mono" data-testid={`inv-cost-${row.slug}`} />
        </div>
      </td>
      <td className="px-4 py-3 font-mono">{money(row.sell_price_cents)}</td>
      <td className="px-4 py-3 font-mono">{money(row.retail_value_cents)}</td>
      <td className="px-4 py-3 font-mono">{money(row.margin_unit_cents)}</td>
      <td className="px-4 py-3">
        <span className={`font-mono text-sm ${row.margin_pct >= 60 ? "text-blue-400" : row.margin_pct >= 30 ? "text-white" : "text-red-400"}`}>{row.margin_pct}%</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-prm !py-1 !px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`inv-save-${row.slug}`}
          >
            {saving ? "…" : "Save"}
          </button>
          {dirty && (
            <button
              onClick={reset}
              className="text-xs text-zinc-500 hover:text-white"
              data-testid={`inv-reset-${row.slug}`}
              title="Discard changes"
            >
              Reset
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminApp() {
  return (
    <Routes>
      <Route index element={<Dashboard />} />
      <Route path="products" element={<Products />} />
      <Route path="inventory" element={<Inventory />} />
      <Route path="orders" element={<Orders />} />
      <Route path="customers" element={<Customers />} />
      <Route path="affiliates" element={<Affiliates />} />
      <Route path="applications" element={<Applications />} />
      <Route path="commissions" element={<Commissions />} />
      <Route path="discounts" element={<Discounts />} />
      <Route path="coas" element={<COAs />} />
      <Route path="batches" element={<Batches />} />
      <Route path="audit-log" element={<AuditLog />} />
      <Route path="settings" element={<Settings />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
